import {
  differenceInMonths,
  differenceInWeeks,
  endOfWeek,
  isAfter,
  isBefore,
  isSameDay,
  isValid,
  startOfDay,
  startOfWeek,
  subWeeks,
} from 'date-fns';
import { IntlShape } from 'react-intl';
import { dateRange } from 'config/dateRange';
import { WEEKLY_START_DAY_OPTIONS } from './types';

export const WEEKLY_MAX_RANGE_WEEKS = 12;

export interface ValidationErrorStatus {
  startDateHelperText?: string;
  endDateHelperText?: string;
  error: boolean;
}

export type OnValidateCallbackType = (
  startDate: Date,
  endDate: Date,
  isComparison: boolean,
  isWeekly?: boolean
) => ValidationErrorStatus;

export interface DateConstraints {
  minStartDate: Date;
  maxStartDate: Date;
  minEndDate: Date;
  maxEndDate: Date;
}

const isValidDate = (date: Date | undefined): date is Date => !!date && isValid(date);

export const isDateDisabled = (date: Date) => {
  const today = startOfDay(new Date(Date.now()));
  return (
    isAfter(date, today) || isSameDay(date, today) || differenceInMonths(today, date) >= dateRange.MAX_MONTH_DATE_RANGE
  );
};

export function isValidComparisonRange(range: { presetValue?: string; from?: Date; to?: Date }): boolean {
  return (
    range.presetValue === 'no_comparison' ||
    (!!range.from && !!range.to && !isDateDisabled(range.from) && !isDateDisabled(range.to))
  );
}

const isRangeValid = (
  from: Date,
  to: Date,
  isWeekly: boolean,
  intl: IntlShape,
  dateConstraints?: DateConstraints
): ValidationErrorStatus => {
  const validationObj = {
    startDateHelperText: '',
    endDateHelperText: '',
    error: false,
  } as ValidationErrorStatus;

  if (isBefore(to, from)) {
    validationObj.startDateHelperText = intl.formatMessage({
      id: 'datepicker.error.startDateBeforeEnd',
      defaultMessage: 'Start date must be before the end date',
    });
    validationObj.endDateHelperText = intl.formatMessage({
      id: 'datepicker.error.endDateAfterStart',
      defaultMessage: 'End date must be after the start date',
    });
    validationObj.error = true;
    return validationObj;
  }

  const today = startOfDay(new Date(Date.now()));
  const maxMonthDateRange = dateRange.MAX_MONTH_DATE_RANGE;

  if (dateConstraints) {
    if (isAfter(from, dateConstraints.maxStartDate)) {
      validationObj.startDateHelperText = intl.formatMessage(
        { id: 'datepicker.error.startDateTooLate.benchmarking' },
        { maxDate: intl.formatDate(dateConstraints.maxStartDate, { dateStyle: 'short' }) }
      );
      validationObj.error = true;
    }
    if (isBefore(from, dateConstraints.minStartDate)) {
      validationObj.startDateHelperText = intl.formatMessage(
        { id: 'datepicker.error.startDateTooEarly.benchmarking' },
        { minDate: intl.formatDate(dateConstraints.minStartDate, { dateStyle: 'short' }) }
      );
      validationObj.error = true;
    }
    if (isAfter(to, dateConstraints.maxEndDate)) {
      validationObj.endDateHelperText = intl.formatMessage(
        { id: 'datepicker.error.endDateTooLate.benchmarking' },
        { maxDate: intl.formatDate(dateConstraints.maxEndDate, { dateStyle: 'short' }) }
      );
      validationObj.error = true;
    }
    if (isBefore(from, dateConstraints.minStartDate)) {
      validationObj.endDateHelperText = intl.formatMessage(
        { id: 'datepicker.error.endDateTooEarly.benchmarking' },
        { minDate: intl.formatDate(dateConstraints.minEndDate, { dateStyle: 'short' }) }
      );
      validationObj.error = true;
    }
  } else {
    if (isSameDay(from, today) || isAfter(from, today) || differenceInMonths(today, from) >= maxMonthDateRange) {
      validationObj.startDateHelperText = intl.formatMessage(
        {
          id: 'datepicker.error.startDateTooOld',
          defaultMessage: 'Start date must be within the past {maxMonths} months.',
        },
        { maxMonths: maxMonthDateRange }
      );
      validationObj.error = true;
    }

    if (isSameDay(to, today) || isAfter(to, today) || differenceInMonths(today, to) >= maxMonthDateRange) {
      validationObj.endDateHelperText = intl.formatMessage(
        {
          id: 'datepicker.error.endDateTooOld',
          defaultMessage: 'End date must be within the past {maxMonths} months.',
        },
        { maxMonths: maxMonthDateRange }
      );
      validationObj.error = true;
    }
  }

  if (isWeekly && differenceInWeeks(to, from) > WEEKLY_MAX_RANGE_WEEKS) {
    validationObj.endDateHelperText = intl.formatMessage(
      {
        id: 'datepicker.error.weekly.max.range.exceeded',
        defaultMessage: `Range must not exceed ${WEEKLY_MAX_RANGE_WEEKS} weeks...`,
      },
      { maxWeeks: WEEKLY_MAX_RANGE_WEEKS }
    );
    validationObj.error = true;
  }

  return validationObj;
};

export const areDatesValid = (from: Date, to: Date, intl: IntlShape): ValidationErrorStatus => {
  const validationObj = {
    startDateHelperText: '',
    endDateHelperText: '',
    error: false,
  };

  const invalidDateMessage = intl.formatMessage({
    id: 'datepicker.error.invalidDate',
    defaultMessage: 'Invalid date',
  });

  if (!isValidDate(from)) {
    validationObj.startDateHelperText = invalidDateMessage;
    validationObj.error = true;
  }

  if (!isValidDate(to)) {
    validationObj.endDateHelperText = invalidDateMessage;
    validationObj.error = true;
  }

  return validationObj;
};

export const isValidDateRange = (from: Date, to: Date, isWeekly: boolean, intl: IntlShape): ValidationErrorStatus => {
  const validDates = areDatesValid(from, to, intl);
  if (validDates.error) {
    return validDates;
  }

  return isRangeValid(from, to, isWeekly, intl);
};

export const isValidBenchmarkDateRange = (isComparison: boolean) => {
  return (from: Date, to: Date, isWeekly: boolean, intl: IntlShape): ValidationErrorStatus => {
    const maxEndDate = endOfWeek(subWeeks(new Date(), 1), WEEKLY_START_DAY_OPTIONS);
    const minStartDate = startOfWeek(subWeeks(maxEndDate, isComparison ? 96 : 44), WEEKLY_START_DAY_OPTIONS);

    const constraints: DateConstraints = {
      minStartDate: minStartDate,
      maxStartDate: startOfWeek(maxEndDate, WEEKLY_START_DAY_OPTIONS),
      maxEndDate: maxEndDate,
      minEndDate: endOfWeek(minStartDate, WEEKLY_START_DAY_OPTIONS),
    };

    const validDates = areDatesValid(from, to, intl);
    if (validDates.error) {
      return validDates;
    }

    return isRangeValid(from, to, isWeekly, intl, constraints);
  };
};
